// eslint-disable-next-line import/no-cycle
import { getClient, toCacheId } from "lib/apolloClient";
import type {
  DigitalGiftInput,
  DigitalGiftType,
  DiscountStateEnum,
  DiscountType,
  ItemInput,
  ItemType,
  MembershipType,
  VariantType
} from "@shared/graphql.types";
import { v4 as uuid } from "uuid";
import { MEMBERSHIP_FRAGMENT, VARIANT_FRAGMENT } from "./fragments";

//------------------------------------------------------------------------------------------------------------------

export function getVariant(id: string): VariantType {
  const result = getClient().readFragment<VariantType>({
    id: toCacheId("VariantType", id),
    fragment: VARIANT_FRAGMENT,
    fragmentName: "VariantFragment"
  });

  if (!result) console.error(`could not find variant: ${id} in cache`);

  return result as VariantType;
}

export function getMembership(id: string): MembershipType {
  const result = getClient().readFragment<MembershipType>({
    id: toCacheId("MembershipType", id ?? "none:none"),
    fragment: MEMBERSHIP_FRAGMENT,
    fragmentName: "MembershipFragment"
  });

  if (!result) console.error(`could not find membership: ${id} in cache`);

  return result as MembershipType;
}

//------------------------------------------------------------------------------------------------------------------

export function toPromoCode(discount: DiscountType): string | null {
  return discount?.code ?? null;
}

export function toDiscountId(promocode: string): string {
  return `__${promocode}__`;
}

export function toDiscount(promocode: string): DiscountType {
  if (!promocode) throw new Error(`toDiscount(): received null item`);

  return {
    id: toDiscountId(promocode),
    code: promocode,
    state: "indeterminate" as DiscountStateEnum,
    effects: []
  };
}

export function toPromoCodes(discounts: DiscountType[] | null): string[] {
  if (!discounts) return [];

  return discounts.map((v) => v.code).filter((v) => !!v) as string[];
}

export function toDigitalGiftInput(
  gift: DigitalGiftType | null
): DigitalGiftInput | null {
  if (!gift) return null;

  return {
    ...gift,
    __typename: undefined
  } as DigitalGiftInput;
}

export function toDigitalGiftType(
  gift: DigitalGiftInput | null
): DigitalGiftType | null {
  if (!gift) return null;

  return {
    ...gift,
    __typename: undefined
  } as DigitalGiftType;
}

export function toItemInput(item: ItemType): ItemInput {
  if (!item) throw new Error(`toItemInput(): received null item`);

  return {
    id: item.id,
    type: item.type,
    membershipId: item.membership?.id ?? null,
    variantId: item.variant?.id ?? null,
    variantSku: item.variant?.sku ?? null,
    quantity: item.quantity,
    maxItemQuantity: item.variant?.maxItemQuantity ?? null,
    giftInfo: toDigitalGiftInput(item.giftInfo ?? null),
    origin: item?.origin ?? "user"
  };
}

export const variantToItemType = (variant: VariantType): ItemType => {
  return {
    id: uuid(),
    variant,
    membership: null,
    quantity: 1,
    type: "product",
    origin: "user"
  };
};

export const membershipToItemType = (membership: MembershipType): ItemType => {
  return {
    id: uuid(),
    variant: null,
    membership,
    quantity: 1,
    type: "membership",
    origin: "user"
  };
};

export const toPimId = (item: ItemInput): string => {
  return item.variantId || item.membershipId || "";
};
